/*
 * The route guard (docs/UI_HUBS_AND_GESTURES_PLAN.md 1.2).
 *
 * <Guarded path="/users"><Users /></Guarded> shows the page to whoever
 * utils/hubs/registry.js lets open that path (strict hubs, decision D19), and
 * NotAllowed in its place to everyone else. It never redirects: the address
 * stays, so a link someone shared still says where it pointed, and Back still
 * works. It takes only the route's own path string, so the routes in App.js
 * stay literal JSX (scripts/prod_docs/tests/test_generate_prod_docs.py reads
 * them with a regex).
 *
 * This is presentation. The server checks every request; a page that slipped
 * past the guard would still be refused its data.
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { pageLabel, pathAllowed } from '../utils/hubs/registry';
import { useIssueReport } from './issues/IssueReportProvider';
import Button, { buttonStyle } from './ui/Button';

/**
 * A page's heading, what happened, the way Home and (inside the app's issue
 * provider) Report an issue. Shared by NotAllowed and pages/NotFound.js.
 */
export function PageNotice({ testId, title, children, homeLabel, reportLabel }) {
  const report = useIssueReport();
  return (
    <section className="page-notice" data-testid={testId} aria-labelledby={`${testId}-title`}>
      <h1 id={`${testId}-title`} tabIndex={-1}>{title}</h1>
      {children}
      <div className="page-notice-actions">
        <Link to="/" style={{ ...buttonStyle('primary'), textDecoration: 'none' }} data-testid={`${testId}-home`}>
          {homeLabel}
        </Link>
        {report && (
          <Button variant="secondary" onClick={report.openReport} aria-haspopup="dialog" data-testid={`${testId}-report`}>
            {reportLabel}
          </Button>
        )}
      </div>
    </section>
  );
}

/** What a person whose role does not open `path` sees in the page's place. */
export function NotAllowed({ path }) {
  const { t } = useTranslation(['hubs', 'nav']);
  const label = pageLabel(path);
  return (
    <PageNotice
      testId="not-allowed"
      title={label ? t('notAllowed.title', { page: t(label) }) : t('notAllowed.titleUnknown')}
      homeLabel={t('notAllowed.home')}
      reportLabel={t('notAllowed.report')}
    >
      <p>{t('notAllowed.body')}</p>
      <p className="page-notice-help">{t('notAllowed.help')}</p>
    </PageNotice>
  );
}

export function Guarded({ path, children }) {
  const { user } = useAuth();
  if (pathAllowed(user, path)) return children;
  return <NotAllowed path={path} />;
}

export default Guarded;
